import React, { useContext, useState } from 'react';
import { Box, SpeedDialAction } from '@mui/material';
import type { SxProps, Theme } from '@mui/material/styles';
import AssignmentIndIcon from '@mui/icons-material/AssignmentInd';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import WorkIcon from '@mui/icons-material/Work';
import { MH_EMPLOYER_ROLES, MH_ROLES } from 'services/auth';
import { Role } from 'API';
import UserContext from 'shared/contexts/UserContext';
import { DICTIONARY } from 'shared/constants';
import AssessmentIcon from '@mui/icons-material/Assessment';
import { useNavigate } from 'react-router';
import CompanyModal from './modals/CompanyModal';
import SpeedDial from './SpeedDial';
import CreateProcessModal from './modals/CreateProcessModal';
import CreatePersonModal from './modals/CreatePersonModal';
import InitiateCaseModal from './modals/InititiateCaseModal/InitiateCaseModal';

const personAddIcon = <PersonAddIcon />;
const workIcon = <WorkIcon />;
const assignmentIndIcon = <AssignmentIndIcon />;
const assessmentIcon = <AssessmentIcon />;

const containerSx: SxProps<Theme> = (theme) => ({
  height: '48px',
  zIndex: 0,
  '& .MuiFab-root': { height: '48px', minHeight: 0, width: '48px' },
  [theme.breakpoints.down('md')]: {
    height: '32px',
    width: '32px',
    '& .MuiFab-sizeSmall': { height: '32px', width: '32px' },
  },
});

const CreateSpeedDial: React.FC = () => {
  //* State
  const [userState] = useContext(UserContext);
  const { role } = userState;
  const navigate = useNavigate();
  const [caseOpen, setCaseOpen] = useState(false);
  const [companyOpen, setCompanyOpen] = useState(false);
  const [personOpen, setPersonOpen] = useState(false);
  const [processOpen, setProcessOpen] = useState(false);

  //* Variables
  const canCreateCompany = MH_ROLES.includes(role);
  const canCreatePerson = MH_ROLES.includes(role);
  const canCreateCase = MH_ROLES.includes(role);
  const canCreateProcess = role === Role.EMPLOYER;
  const canCreateReport = MH_EMPLOYER_ROLES.includes(role);

  if (!canCreateCompany && !canCreatePerson && !canCreateProcess) {
    return null;
  }

  return (
    <Box component="section" sx={containerSx}>
      <SpeedDial label="Create">
        {canCreatePerson && (
          <SpeedDialAction
            icon={personAddIcon}
            tooltipTitle={DICTIONARY.CreateNewUserTooltip}
            onClick={() => setPersonOpen(true)}
          />
        )}

        {canCreateCompany && (
          <SpeedDialAction
            icon={workIcon}
            tooltipTitle={DICTIONARY.CreateCompanyTooltip}
            onClick={() => setCompanyOpen(true)}
          />
        )}

        {canCreateCase && (
          <SpeedDialAction
            icon={assignmentIndIcon}
            tooltipTitle={DICTIONARY.CreateNewCaseTooltip}
            onClick={() => setCaseOpen(true)}
          />
        )}

        {canCreateProcess && (
          <SpeedDialAction
            icon={assignmentIndIcon}
            tooltipTitle={DICTIONARY.CreateNewProcessRequestTooltip}
            onClick={() => setProcessOpen(true)}
          />
        )}

        {canCreateReport && (
          <SpeedDialAction
            icon={assessmentIcon}
            tooltipTitle={DICTIONARY.CreateNewReport}
            onClick={() => navigate('/create-report')}
          />
        )}
      </SpeedDial>

      {canCreateCompany && companyOpen && (
        <CompanyModal open={companyOpen} setOpen={setCompanyOpen} />
      )}

      {canCreatePerson && personOpen && (
        <CreatePersonModal
          open={personOpen}
          setOpen={setPersonOpen}
          createPersonConfig={{
            createRole: Role.FOREIGNNATIONAL,
          }}
        />
      )}

      {canCreateCase && caseOpen && (
        <InitiateCaseModal
          open={caseOpen}
          setOpen={setCaseOpen}
        />
      )}

      {canCreateProcess && processOpen && (
        <CreateProcessModal open={processOpen} setOpen={setProcessOpen} />
      )}
    </Box>
  );
};

export default CreateSpeedDial;
